import { TextDeltaOp } from '@sailfish-rrweb/types';
import type { TextDelta } from '@sailfish-rrweb/types';

/**
 * Result of comparing two strings for text delta
 */
export interface DiffResult {
  delta: TextDelta | null;
  /** Estimated byte size of the delta representation */
  deltaSize: number;
  /** Byte size of full text representation */
  fullTextSize: number;
}

/**
 * Estimates the JSON serialization size of an object
 */
function estimateJsonSize(obj: unknown): number {
  return JSON.stringify(obj).length;
}

/**
 * Computes the diff between oldText and newText.
 * Returns the most efficient delta operation.
 *
 * Algorithm:
 * 1. Check for simple append (most common case for streaming)
 * 2. Check for simple delete from end (backspace)
 * 3. Find common prefix and suffix to identify middle change
 * 4. Create insert/delete/replace as appropriate
 */
export function computeTextDiff(
  oldText: string | null,
  newText: string | null,
): DiffResult {
  const oldStr = oldText || '';
  const newStr = newText || '';
  const fullTextSize = estimateJsonSize({ value: newStr });

  // Empty old text - always use full text (establishes baseline)
  if (oldStr.length === 0) {
    return { delta: null, deltaSize: Infinity, fullTextSize };
  }

  // No change
  if (oldStr === newStr) {
    return { delta: null, deltaSize: Infinity, fullTextSize };
  }

  // Complete clear - full text is cleaner
  if (newStr.length === 0) {
    return { delta: null, deltaSize: Infinity, fullTextSize };
  }

  // Case 1: Simple append (most common - streaming text)
  if (newStr.startsWith(oldStr)) {
    const appendedText = newStr.slice(oldStr.length);
    const delta: TextDelta = {
      op: TextDeltaOp.Append,
      text: appendedText,
    };
    const deltaSize = estimateJsonSize({ delta });
    return { delta, deltaSize, fullTextSize };
  }

  // Case 2: Simple delete from end (backspace)
  if (oldStr.startsWith(newStr)) {
    const deleteLength = oldStr.length - newStr.length;
    const delta: TextDelta = {
      op: TextDeltaOp.Delete,
      pos: newStr.length,
      length: deleteLength,
    };
    const deltaSize = estimateJsonSize({ delta });
    return { delta, deltaSize, fullTextSize };
  }

  // Case 3: Find common prefix
  let commonPrefixLen = 0;
  const minLen = Math.min(oldStr.length, newStr.length);
  while (
    commonPrefixLen < minLen &&
    oldStr[commonPrefixLen] === newStr[commonPrefixLen]
  ) {
    commonPrefixLen++;
  }

  // Case 4: Find common suffix (from the non-prefix portion)
  let commonSuffixLen = 0;
  const oldRemaining = oldStr.length - commonPrefixLen;
  const newRemaining = newStr.length - commonPrefixLen;
  const maxSuffixLen = Math.min(oldRemaining, newRemaining);
  while (
    commonSuffixLen < maxSuffixLen &&
    oldStr[oldStr.length - 1 - commonSuffixLen] ===
      newStr[newStr.length - 1 - commonSuffixLen]
  ) {
    commonSuffixLen++;
  }

  // Determine what changed in the middle
  const oldMiddleLen = oldStr.length - commonPrefixLen - commonSuffixLen;
  const newMiddleLen = newStr.length - commonPrefixLen - commonSuffixLen;
  const newMiddleText = newStr.slice(
    commonPrefixLen,
    newStr.length - commonSuffixLen,
  );

  let delta: TextDelta;

  if (oldMiddleLen === 0 && newMiddleLen > 0) {
    // Pure insert
    delta = {
      op: TextDeltaOp.Insert,
      pos: commonPrefixLen,
      text: newMiddleText,
    };
  } else if (oldMiddleLen > 0 && newMiddleLen === 0) {
    // Pure delete
    delta = {
      op: TextDeltaOp.Delete,
      pos: commonPrefixLen,
      length: oldMiddleLen,
    };
  } else {
    // Replace
    delta = {
      op: TextDeltaOp.Replace,
      pos: commonPrefixLen,
      deleteCount: oldMiddleLen,
      text: newMiddleText,
    };
  }

  const deltaSize = estimateJsonSize({ delta });
  return { delta, deltaSize, fullTextSize };
}

/**
 * Determines if delta should be used based on size comparison.
 * Includes a threshold to prefer full text when sizes are similar
 * (full text is simpler and more debuggable).
 */
export function shouldUseDelta(result: DiffResult): boolean {
  if (!result.delta) return false;
  // Use delta only if it saves at least 20% space
  return result.deltaSize < result.fullTextSize * 0.8;
}

/**
 * Applies a delta operation to the current text to produce new text.
 * Used during replay to reconstruct the value.
 */
export function applyTextDelta(
  currentText: string | null,
  delta: TextDelta,
): string {
  const text = currentText || '';

  switch (delta.op) {
    case TextDeltaOp.Append:
      return text + delta.text;

    case TextDeltaOp.Insert:
      return text.slice(0, delta.pos) + delta.text + text.slice(delta.pos);

    case TextDeltaOp.Delete:
      return text.slice(0, delta.pos) + text.slice(delta.pos + delta.length);

    case TextDeltaOp.Replace:
      return (
        text.slice(0, delta.pos) +
        delta.text +
        text.slice(delta.pos + delta.deleteCount)
      );

    default:
      // Unknown operation - return unchanged (defensive)
      return text;
  }
}
